
import React from 'react';
import { Routes, Route } from 'react-router-dom';
import { LandingPage } from '../landing/LandingPage';
import { RootRedirect } from './RootRedirect';
import CreateUsername from '@/pages/CreateUsername';
import { ProtectedRoute } from './ProtectedRoute';
import { AdminRoute } from './AdminRoute';
import Bulletins from '@/pages/Bulletins';
import Profile from '@/pages/Profile';
import MiniProfile from '@/pages/MiniProfile';
import Invites from '@/pages/Invites';
import Videos from '@/pages/Videos';
import VideoDetails from '@/pages/VideoDetails';
import Leaderboard from '@/pages/Leaderboard';
import UserProfile from '@/pages/UserProfile';
import NeedInvite from '@/pages/NeedInvite';
import Auth from '@/pages/Auth';
import AuthCallback from '@/pages/AuthCallback';
import Logout from '@/pages/Logout';
import Premium from '@/pages/Premium';
import { TestRunner } from '@/test/TestRunner';
import Roadmap from '@/pages/Roadmap';
import AdminList from '@/pages/AdminList';
import AdminDashboard from '@/pages/AdminDashboard';
import OffTheRecord from '@/pages/OffTheRecord';
import Settings from '@/pages/Settings';
import { LinkDiscord } from '@/pages/LinkDiscord';
import Leaving from '@/pages/Leaving';
import BannedPage from '@/pages/BannedPage';
import BentoProfile from '@/pages/BentoProfile';
import Chrono from '@/pages/Chrono';
import Relics from '@/pages/Relics';
import RelicsTest from '@/pages/RelicsTest';
import BulletinsTest from '@/pages/BulletinsTest';
import DropsCreate from '@/pages/DropsCreate';
import DropsSuccess from '@/pages/DropsSuccess';
import DropsCancel from '@/pages/DropsCancel';
import DropsDashboard from '@/pages/DropsDashboard';
import DropsClaims from '@/pages/DropsClaims';
import DropsInventory from '@/pages/DropsInventory';
import DropsAdmin from '@/pages/DropsAdmin';
import DropsCollectionDetail from '@/pages/DropsCollectionDetail';
import DropsEngineWiki from '@/pages/DropsEngineWiki';
import DropsProbabilityAdmin from '@/pages/DropsProbabilityAdmin';
import SplineLandingPage from '@/pages/SplineLandingPage';
import FomoCanvas from '@/pages/FomoCanvas';
import { CanvasModeProvider } from '@/contexts/CanvasModeContext';
import Contextboard from '@/pages/Contextboard';
import TopFollows from '@/pages/TopFollows';
import LoopsPage from '@/pages/LoopsPage';
import LoopsWithTransitions from '@/pages/LoopsWithTransitions';
import CommunitiesListPageWithLayout from '@/pages/CommunitiesListPageWithLayout';
import CommunityPageWithLayout from '@/pages/CommunityPageWithLayout';
import { MoodboardView } from '@/components/moodboards/MoodboardView';
import { AlbumView } from '@/components/albums/AlbumView';
import MoodboardDetail from '@/pages/MoodboardDetail';
import MoodboardsList from '@/pages/MoodboardsList';
import HomeLanding from '@/pages/HomeLanding';
import { PreviewRoute } from './PreviewRoute';

export const AppRoutes = () => {
  return (
    <Routes>
      {/* Banned page must be publicly accessible */}
      <Route path="/banned" element={<BannedPage />} />
      
      {/* Public routes that don't need authentication */}
      <Route path="/home" element={<HomeLanding />} />
      <Route path="/landing" element={<LandingPage />} />
      <Route path="/spline-landing" element={<SplineLandingPage />} />
      <Route path="/auth" element={<Auth />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/logout" element={<Logout />} />
      <Route path="/roadmap" element={<Roadmap />} />
      <Route path="/need-invite" element={<NeedInvite />} />
      <Route path="/mini/:username" element={<MiniProfile />} />
      
      {/* Root path handling */}
      <Route path="/" element={<RootRedirect />} />
      
      {/* Routes that need authentication but not profile */}
      <Route path="/create-username" element={<CreateUsername />} />
      
      {/* Protected routes */}
      <Route path="/bulletins" element={
        <PreviewRoute title="Welcome to Artstream" description="Login to post your artwork and discover like minded communities">
          <BulletinsTest />
        </PreviewRoute>
      } />
      <Route path="/bulletins/:tab" element={
        <PreviewRoute title="Welcome to Artstream" description="Login to post your artwork and discover like minded communities">
          <BulletinsTest />
        </PreviewRoute>
      } />
      <Route path="/bulletins/:tab/:category" element={
        <PreviewRoute title="Welcome to Artstream" description="Login to post your artwork and discover like minded communities">
          <BulletinsTest />
        </PreviewRoute>
      } />
      <Route path="/bulletins/:tab/:category/:bulletinId" element={
        <PreviewRoute title="Welcome to Artstream" description="Login to post your artwork and discover like minded communities">
          <BulletinsTest />
        </PreviewRoute>
      } />
      <Route path="/loops" element={
        <PreviewRoute title="Welcome to Loops" description="Login to share moments and connect with creators">
          <LoopsPage />
        </PreviewRoute>
      } />
      <Route path="/loops/:mode" element={
        <PreviewRoute title="Welcome to Loops" description="Login to share moments and connect with creators">
          <LoopsPage />
        </PreviewRoute>
      } />
      <Route path="/loops-transitions" element={
        <ProtectedRoute>
          <LoopsWithTransitions />
        </ProtectedRoute>
      } />
      <Route path="/communities" element={
        <PreviewRoute title="Welcome to Communities" description="Login to join communities and connect with artists">
          <CommunitiesListPageWithLayout />
        </PreviewRoute>
      } />
      <Route path="/community/:communityName" element={
        <PreviewRoute title="Join this Community" description="Login to participate and connect with members">
          <CommunityPageWithLayout />
        </PreviewRoute>
      } />
      <Route path="/profile" element={
        <ProtectedRoute>
          <Profile />
        </ProtectedRoute>
      } />
      <Route path="/profile/:username" element={
        <ProtectedRoute>
          <UserProfile />
        </ProtectedRoute>
      } />
      <Route path="/user/:username" element={
        <ProtectedRoute>
          <UserProfile />
        </ProtectedRoute>
      } />
      <Route path="/2/:username" element={
        <ProtectedRoute>
          <BentoProfile />
        </ProtectedRoute>
      } />
      <Route path="/invites" element={
        <ProtectedRoute>
          <Invites />
        </ProtectedRoute>
      } />
      <Route path="/videos" element={
        <ProtectedRoute>
          <Videos />
        </ProtectedRoute>
      } />
      <Route path="/videos/:id" element={
        <ProtectedRoute>
          <VideoDetails />
        </ProtectedRoute>
      } />
      <Route path="/leaderboard" element={
        <ProtectedRoute>
          <Leaderboard />
        </ProtectedRoute>
      } />
      <Route path="/premium" element={
        <ProtectedRoute>
          <Premium />
        </ProtectedRoute>
      } />
      <Route path="/drops" element={
        <ProtectedRoute>
          <DropsDashboard />
        </ProtectedRoute>
      } />
      <Route path="/drops/:collectionId/claims" element={
        <ProtectedRoute>
          <DropsClaims />
        </ProtectedRoute>
      } />
      <Route path="/drops/create" element={
        <ProtectedRoute>
          <DropsCreate />
        </ProtectedRoute>
      } />
      <Route path="/drops/success" element={
        <ProtectedRoute>
          <DropsSuccess />
        </ProtectedRoute>
      } />
      <Route path="/drops/cancel" element={
        <ProtectedRoute>
          <DropsCancel />
        </ProtectedRoute>
      } />
      <Route path="/drops/inventory" element={
        <ProtectedRoute>
          <DropsInventory />
        </ProtectedRoute>
      } />
      <Route path="/drops/admin" element={
        <ProtectedRoute>
          <DropsAdmin />
        </ProtectedRoute>
      } />
      <Route path="/drops/admin/wiki" element={
        <AdminRoute>
          <DropsEngineWiki />
        </AdminRoute>
      } />
      <Route path="/drops/admin/probabilities" element={
        <AdminRoute>
          <DropsProbabilityAdmin />
        </AdminRoute>
      } />
      <Route path="/drops/:collectionId" element={
        <DropsCollectionDetail />
      } />
      <Route path="/chrono" element={
        <ProtectedRoute>
          <Chrono />
        </ProtectedRoute>
      } />
      <Route path="/chrono/:bulletinId" element={
        <ProtectedRoute>
          <Chrono />
        </ProtectedRoute>
      } />
      <Route path="/relics" element={
        <ProtectedRoute>
          <Relics />
        </ProtectedRoute>
      } />
      <Route path="/relics/:bulletinId" element={
        <ProtectedRoute>
          <Relics />
        </ProtectedRoute>
      } />
      <Route path="/relics-test" element={
        <ProtectedRoute>
          <RelicsTest />
        </ProtectedRoute>
      } />
      <Route path="/canvas" element={
        <ProtectedRoute>
          <CanvasModeProvider>
            <FomoCanvas />
          </CanvasModeProvider>
        </ProtectedRoute>
      } />
      <Route path="/contextboard" element={
        <ProtectedRoute>
          <Contextboard />
        </ProtectedRoute>
      } />
      <Route path="/top-follows" element={
        <PreviewRoute title="Discover Top Creators" description="Login to follow and connect with artists">
          <TopFollows />
        </PreviewRoute>
      } />
      <Route path="/moodboards" element={
        <PreviewRoute title="Explore Moodboards" description="Login to create and save your own collections">
          <MoodboardsList />
        </PreviewRoute>
      } />
      <Route path="/moodboards/:id" element={
        <PreviewRoute title="View Moodboard" description="Login to interact with this collection">
          <MoodboardDetail />
        </PreviewRoute>
      } />
      <Route path="/album/:id" element={
        <ProtectedRoute>
          <AlbumView />
        </ProtectedRoute>
      } />
      <Route path="/bulletins-old" element={
        <ProtectedRoute>
          <Bulletins />
        </ProtectedRoute>
      } />
      <Route path="/bulletins-old/:tab" element={
        <ProtectedRoute>
          <Bulletins />
        </ProtectedRoute>
      } />
      <Route path="/bulletins-old/:tab/:category" element={
        <ProtectedRoute>
          <Bulletins />
        </ProtectedRoute>
      } />
      <Route path="/bulletins-old/:tab/:category/:bulletinId" element={
        <ProtectedRoute>
          <Bulletins />
        </ProtectedRoute>
      } />
      <Route path="/bulletins-test" element={
        <ProtectedRoute>
          <BulletinsTest />
        </ProtectedRoute>
      } />
      <Route path="/otr" element={
        <ProtectedRoute>
          <OffTheRecord />
        </ProtectedRoute>
      } />
      <Route path="/settings" element={
        <ProtectedRoute>
          <Settings />
        </ProtectedRoute>
      } />
      <Route path="/link-discord" element={
        <ProtectedRoute>
          <LinkDiscord />
        </ProtectedRoute>
      } />
      <Route path="/leaving" element={
        <ProtectedRoute>
          <Leaving />
        </ProtectedRoute>
      } />
      
      {/* Admin routes */}
      <Route path="/test-runner" element={
        <AdminRoute>
          <TestRunner />
        </AdminRoute>
      } />
      <Route path="/admin" element={
        <AdminRoute>
          <AdminDashboard />
        </AdminRoute>
      } />
      <Route path="/admin/list" element={
        <AdminRoute>
          <AdminList />
        </AdminRoute>
      } />
    </Routes>
  );
};
